<!--
Formular für einen neuen Zertifikatantrag.
-->
<template>
  <div>
    <!-- 1 -->
    <div v-show="props.step === 1">
      <h1>{{ t(props.certType.name + "_title") }}</h1>
      <p>{{ t("intro") }}</p>
      <!-- Vorrübergehende Mitteilung zur Abkündigung der Serverzertifikate
          in der Global Policy, siehe Ticket #2049 -->
      <div>
        <p v-if="custom.policy.showNotification" style="color: red">
          {{ t("notification") }}
        </p>
      </div>

      <!-- Profilauswahl -->
      <label for="role">{{ t("profile") }}</label>
      &nbsp;
      <select id="role" v-model="role" @change="updatePublish()">
        <option v-for="r in roles" :key="r">{{ r }}</option>
      </select>
      &nbsp;
      <p v-html="t('tooltip_profile', { profiles_hint: profiles_hint })"></p>

      <!-- PKCS10 Upload -->
      <div v-if="isPKCS10Upload()">
        <Pkcs10Upload
          v-model="pkcs10"
          :certRole="role"
          :displayAllFeedback="displayFeedback"
          :keytypeOptions="backendKeytypeOptions"
          @error-message="(msg) => emit('error-message', msg)"
        ></Pkcs10Upload>
      </div>
      <!-- Zertifikatsdaten -->
      <div v-else>
        <NewRequestCertificate
          v-model="certificateData"
          :certType="props.certType"
          :displayAllFeedback="displayFeedback"
          :keytypeOptions="keytypeOptions"
          @error-message="(msg) => emit('error-message', msg)"
        ></NewRequestCertificate>
      </div>
      <!-- Sperr-PIN -->
      <NewRequestApplicant
        v-model="userData"
        :certType="props.certType"
        :displayAllFeedback="displayFeedback"
      ></NewRequestApplicant>
      <!-- Kommentarfeld -->
      <h2>{{ t("comment") }}</h2>
      <label for="comment_input">{{ t("comment_label") }}</label>
      <b-form-input
        id="comment_input"
        v-model="comment"
        type="text"
        :placeholder="t('comment')"
        trim
      ></b-form-input>
      <!-- Terms and conditions -->
      <!-- not using b-form-checkbox because of https://github.com/bootstrap-vue/bootstrap-vue/issues/7182 -->
      <div v-if="custom.termsAndConditionsRequired" class="custom-control custom-checkbox">
        <input
          id="checkbox-1"
          v-model="termsAccepted"
          type="checkbox"
          class="custom-control-input"
          :class="{
            'is-valid': displayFeedback && termsAccepted,
            'is-invalid': displayFeedback &! termsAccepted
            }"
          name="checkbox-1"
          />
        <label class="custom-control-label" for="checkbox-1">
          <p v-html="termsAndConditions + ' *'"></p>
        </label>
      </div>
      <b-form-invalid-feedback
        v-if="custom.termsAndConditionsRequired"
        id="input-false"
        :state="displayFeedback ? termsAccepted : null"
        >{{ t("terms_and_conditions_feedback") }}</b-form-invalid-feedback
      >
      <!-- Veröffentlichen -->
      <!-- not using b-form-checkbox because of https://github.com/bootstrap-vue/bootstrap-vue/issues/7182 -->
      <div v-if="showPublishCheckbox" class="custom-control custom-checkbox">
        <input
          id="checkbox-2"
          v-model="publish"
          type="checkbox"
          class="custom-control-input"
          :class="{
            'is-valid': displayFeedback && (!publishRequired || publish),
            'is-invalid': displayFeedback && (publishRequired &! publish)
            }"
          name="checkbox-2"
          />
        <label class="custom-control-label" for="checkbox-2">
          <p v-html="t('publish') + (publishRequired ? ' *' : '')"></p>
        </label>
      </div>
      <b-form-invalid-feedback
        :state="displayFeedback ? !publishRequired || publish : null"
        >{{ t("publish_required") }}</b-form-invalid-feedback
      >
      <!-- Datenschutzrichtlinie -->
      <!-- not using b-form-checkbox because of https://github.com/bootstrap-vue/bootstrap-vue/issues/7182 -->
      <div class="custom-control custom-checkbox">
        <input
          id="checkbox-3"
          v-model="dataProtectionAgreementAccepted"
          type="checkbox"
          class="custom-control-input"
          :class="{
            'is-valid': displayFeedback && dataProtectionAgreementAccepted,
            'is-invalid': displayFeedback &! dataProtectionAgreementAccepted
            }"
          name="checkbox-3"
          />
        <label class="custom-control-label" for="checkbox-3">
          <p v-html="dataProtectionAgreement"></p>
        </label>
      </div>
      <b-form-invalid-feedback
        id="input-false"
        :state="displayFeedback ? dataProtectionAgreementAccepted : null"
        >{{ t("terms_and_conditions_feedback") }}</b-form-invalid-feedback
      >
      <b-button
        type="submit"
        :disabled="isUploading"
        variant="primary"
        @click.prevent="onSubmit"
      >
        {{ t("submit") }}
        <b-spinner v-if="isUploading" small label="Small Spinner"></b-spinner>
      </b-button>
    </div>
    <!-- 2: Antrag anzeigen -->
    <div v-if="props.step === 2">
      <ConfirmRequest
        :request="request"
        :certType="props.certType"
        @error-message="(msg) => emit('error-message', msg)"
        @request-saved="displayPDFButton"
      ></ConfirmRequest>
      <b-button id="backbutton" variant="primary" @click="$router.go(-1)">
        {{ t("back_button") }}
      </b-button>
    </div>
    <!-- 3: Abschlussseite, PDF-Download -->
    <DownloadPDF v-if="props.step === 3" :request="request"></DownloadPDF>
  </div>
</template>

<i18n>
    en:
      notification: "Please note: As of 30.08.2023, certificates will no longer be issued in the DFN-PKI Global. In the future, you will receive new certificates via the GÉANT TCS service. Please contact your local subscriber service at your institution for information on specific application procedures."
      profile: "Certificate profile"
      tooltip_profile: "The chosen \"Certificate profile\" determines the possible usages of the certificate.{profiles_hint}"
      profiles_hint_text: "(Description of certificate profiles [German])"
      user_title: "New user certificate (incl. key generation by your webbrowser)"
      pseudonym_title: "New pseudonym certificate (incl. key generation by your webbrowser)"
      group_title: "New group certificate (incl. key generation by your webbrowser)"
      server_title: "New server certificate (incl. key generation by your webbrowser)"
      pkcs10_title: "CSR (PKCS#10) upload"
      intro: "Here you can apply for a new certificate."
      submit: "Next"
      publish: "{'I agree to the publication of the certificate with the contained names and e-mail addresses.<br>You can withdraw this agreement by sending an e-mail to <a href=\"mailto:pki@dfn.de\">pki@dfn.de</a>.'}"
      comment: "Personal note"
      comment_label: "You may enter an optional note for this certificate application here. The comment will solely be saved in your local certificate application data file."
      back_button: "Change data"
      terms_and_conditions_default: "I am commited to comply with the regulations contained in <a href='https://info.pca.dfn.de/doc/Info_Zertifikatinhaber.pdf' target='blank'>Information for certificate holders</a>."
      terms_and_conditions_feedback: "This is a mandatory field"
      publish_required: "You have to agree to the publication of your certificate."
    de:
      notification: "Bitte beachten Sie: Ab 30.08.2023 werden in der DFN-PKI Global keine Zertifikate mehr ausgestellt. Neue Zertifikate erhalten Sie in Zukunft über den Dienst GÉANT TCS. Bitte wenden Sie sich für Informationen zu konkreten Beantragungsverfahren an Ihren lokalen Teilnehmerservice Ihrer Einrichtung."
      profile: "Zertifikatsprofil"
      tooltip_profile: "Mit dem Zertifikatsprofil legen Sie den Einsatzzweck des Zertifikats fest.{profiles_hint}"
      profiles_hint_text: "(Beschreibung der Zertifikatsprofile)"
      user_title: "Neues Nutzerzertifikat (inkl. Schlüsselerzeugung durch Ihren Web-Browser)"
      pseudonym_title: "Neues Pseudonymzertifikat (inkl. Schlüsselerzeugung durch Ihren Web-Browser)"
      group_title: "Neues Gruppenzertifikat (inkl. Schlüsselerzeugung durch Ihren Web-Browser)"
      server_title: "Neues Serverzertifikat (inkl. Schlüsselerzeugung durch Ihren Web-Browser)"
      pkcs10_title: "Eigene CSR-Datei (PKCS#10) einreichen"
      intro: "Hier können Sie ein neues Zertifikat beantragen."
      submit: "Weiter"
      publish: "{'Ich stimme der Veröffentlichung des Zertifikates mit meinem darin enthaltenen Namen und der E-Mail-Adresse zu.<br>Sie können diese Einwilligung jederzeit mit Wirkung für die Zukunft durch eine E-Mail an <a href=\"mailto:pki@dfn.de\">pki@dfn.de</a> widerrufen.'}"
      comment: "Persönliche Notiz"
      comment_label: "Hier können Sie eine persönliche Notiz zu diesem Zertifikatantrag eingeben. Diese Notiz wird ausschließlich lokal mit der Antragsdatei abgespeichert."
      back_button: "Daten ändern"
      terms_and_conditions_default: "Ich verpflichte mich, die in den <a href='https://info.pca.dfn.de/doc/Info_Zertifikatinhaber.pdf' target='blank'>Informationen für Zertifikatinhaber</a> aufgeführten Regelungen einzuhalten."
      terms_and_conditions_feedback: "Dies ist ein Pflichtfeld"
      publish_required: "Sie müssen der Veröffentlichung zustimmen."
</i18n>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n'

import { brand } from "@/brand/brand";
import { custom } from "@/customization/customization";
import { Logger } from "@/logger/logger";
import { CERT_TYPES } from "@/pki/certTypes";
import { sha1 } from "@/pki/dfnpki";
import { RequestBuilder } from "@/pki/requestbuilder";
import { Request } from "@/pki/request.js";

import DownloadPDF from "@/components/pages/new-certificate/DownloadPDF";
import NewRequestApplicant from "@/components/pages/new-certificate/NewRequestApplicant";
import NewRequestCertificate from "@/components/pages/new-certificate/NewRequestCertificate";
import Pkcs10Upload from "@/components/pages/new-certificate/Pkcs10Upload";
import ConfirmRequest from "@/components/pages/new-certificate/ConfirmRequest";

const { t, locale } = useI18n()
const router = useRouter()

const logger = new Logger("NewRequest");

const developerMode = process.env.NODE_ENV === "development";

const emit = defineEmits(['error-message'])
const props = defineProps({
  certType: { type: Object, required: true },
  step: { type: Number, required: true },
})

/**
 *  Schlüsseltypen Default und aus der Custom
 **/
var keytypeOptions = new Array();
logger.debug("curves: " + custom.allowedCurves);
logger.debug("RSA Min: " + custom.rsaMinKeyLength);
// Zusammensetzen von RSA Optionen
const defaultRSAKeyOptions = 
[{ text: "RSA 2048", value: "rsa2048" }, 
{ text: "RSA 4096", value: "rsa4096" }];
const rsaMinKeyLength = custom.rsaMinKeyLength;
switch (rsaMinKeyLength) {
  default:
  case 2048:
    keytypeOptions = keytypeOptions.concat(defaultRSAKeyOptions);
    break;
  case 4096:
    keytypeOptions = keytypeOptions.concat(defaultRSAKeyOptions[1]);
    break;
}

var backendKeytypeOptions = new Array();

// Zusammensetzen von EC Optionen
const defaultCurveOptions = 
[{ text: "EC P-256", value: "secp256r1" }, 
{ text: "EC P-384", value: "secp384r1" }];

if (!custom.allowedCurves || !custom.allowedCurves.length) {
  keytypeOptions = keytypeOptions.concat(defaultCurveOptions);
} else {
  for (let curve of custom.allowedCurves) {
    switch (curve) {
      case "prime256v1":
      case "secp256r1":
        keytypeOptions.push({ text: "EC P-256", value: "secp256r1" });
        continue;
      case "secp384r1":
        keytypeOptions.push({ text: "EC P-384", value: "secp384r1" });
        continue;
      case "secp521r1":
        keytypeOptions.push({ text: "EC P-521", value: "secp521r1" });
        continue;
      default:
        backendKeytypeOptions.push({ text: curve, value: curve });
        logger.info("Die Kurve "+ curve + " wird nicht in den Baseline Requirements vom CAB erlaubt und kann deswegen nicht miteinbezogen werden");
        continue;
    }
  }
}
backendKeytypeOptions = keytypeOptions.concat(backendKeytypeOptions);

const roles =
  props.certType == CERT_TYPES.server
    ? custom.serverRoles
    : props.certType == CERT_TYPES.pkcs10
      ? custom.pkcs10Roles
      : custom.userRoles;

const role = ref(roles[0]) // Das ausgewählte Zertifikatsprofil
const userData = ref(null) // Daten aus der Komponente NewRequestApplicant
const certificateData = ref({
  rdns: [],
  sans: [],
  keytype: "",
}) // Daten aus der Komponente NewRequestCertificate
const pkcs10 = ref(null) // Object {pem, subject, altnames} vom Pkcs10Upload
const termsAccepted = ref(false)
const displayFeedback = ref(false) // Sollen alle Inputs Feedback anzeigen?
const isUploading = ref(false)
const request = ref(null)
const comment = ref("")
const publish = ref(null) // Wert der Veröffentlichen-Checkbox. Siehe updatePublish()
const publishRequired = ref(null) // Muss veröffentlicht werden?
const showPublishCheckbox = ref(null) // Wird Checkbox angezeigt?
const dataProtectionAgreementAccepted = ref(false)

const termsAndConditions = computed(() => {
  let conditions = custom.termsAndConditions[locale.value];
  // es gibt ra_id_<N>.xml configs ohne <terms-and-conditions>, cf. #1826
  // in solch einem Fall hat conditions den Wert ""
  if (!conditions) conditions = t("terms_and_conditions_default");
  return conditions;
})

// Legt den Datenschutztext in Abhängigkeit der Rolle (Server/User) fest
const dataProtectionAgreement = computed(() => {
  let dpa = custom.dataProtectionAgreement;
  let dpaText = dpa.userText[locale.value];

  if (custom.serverRoles.includes(role.value)) {
    dpaText = dpa.serverText[locale.value];
  }

  dpaText = dpaText.replace(
    "{link}",
    '<a href="' +
    dpa.url[locale.value] +
    '" target="_blank" rel="noopener noreferrer">' +
    dpa.linkText[locale.value] +
    "</a>"
  );
  // Pflichtfeld, daher Sternchen anfügen.
  return dpaText + " *";
})

const profiles_hint = computed(() => {
  if (brand.showProfilesHint) {
    return (
      " <a target='_blank' rel='noopener noreferrer' href='" +
      custom.profileHintURL +
      "'>" +
      t("profiles_hint_text", locale.value) +
      "</a>"
    );
  }
  return "";
})

watch(props.step, (newStep, oldStep) => {
  if (oldStep > 2) {
    reset();
  }
})
watch(props.certType, (newType, oldType) => {
  if (newType !== oldType) {
    reset();
  }
})

  // created() {
if (isNaN(props.step) || (props.step != 1 && !request.value)) {
  logger.info("Seiteneinstieg nicht beim Start, mache $router.replace");
  router.replace("1");
}
  // },

// Wechselt auf Seite 3: Abschluss mit PDF-Anzeige.
const displayPDFButton = () => {
  router.push("3");
}

// Setzt alle Felder des Formulars zurück
const reset = () => {
  // TODO: Zurücksetzen der Eingabefelder
  // Checkboxes werden zurückgesetzt
  termsAccepted.value = false;
  dataProtectionAgreementAccepted.value = false;
  // TODO: publishUser neu evaluieren
}

const developerModeFunction = () => {
  termsAccepted.value = true;
  dataProtectionAgreementAccepted.value = true;
}

const isPKCS10Upload = () => {
  return props.certType == CERT_TYPES.pkcs10;
}

/**
 * Setzt die Werte showPublishCheckbox, publish und publishRequired.
 */
const updatePublish = () => {
  let _showPublishCheckbox;
  let _publish;
  let _publishRequired = false;

  // Hier wird ermittelt, ob das Veröffentlichen erforderlich ist, ob eine
  // Checkbox angezeigt wird und ob sie vorangekreuzt ist. Das geht auch
  // mit weniger Code, ist durch das Switch aber expliziter.
  const configuredPublish =
    props.certType == CERT_TYPES.server ||
      custom.serverRoles.includes(role.value)
      ? custom.publishServer
      : custom.publishUser;
  switch (configuredPublish) {
    case "no":
      _showPublishCheckbox = false;
      _publish = false;
      break;
    case "optional_optout":
      _showPublishCheckbox = true;
      _publish = true;
      break;
    case "optional":
      _showPublishCheckbox = true;
      _publish = false;
      break;
    case "required":
      _showPublishCheckbox = true;
      _publish = false;
      _publishRequired = true;
      break;
    case "automatic":
      _showPublishCheckbox = false;
      _publish = true;
      break;
    default:
      logger.warn(
        "Unbekannter Wert " +
        custom.publishUser +
        " für custom.publishUser, nutze 'optional'"
      );
      _showPublishCheckbox = true;
      _publish = false;
  }
  if (brand.neverPublish) {
    // Für DFNCertPKI gelten eigene Regeln.
    _showPublishCheckbox = false;
    _publish = false;
  }

  // Werte tatsächlich setzen
  publish.value = _publish;
  publishRequired.value = _publishRequired;
  showPublishCheckbox.value = _showPublishCheckbox;

  logger.trace(`publish etc. aktualisiert:
        role = ${role.value}
        configuredPublish = ${configuredPublish}
        showPublishCheckbox = ${_showPublishCheckbox}
        publish = ${_publish}
        publishRequired = ${_publishRequired}`);
}

/**
 * Erzeugt aus den Eingabedaten einen Request und wechselt auf Seite 2.
 *
 * Bei Fehlern in der Eingabe wird die Seite nicht gewechselt sondern
 * Feedback an allen Inputs angezeigt.
 */
const onSubmit = async () => {
  logger.debug("onSubmit()");
  displayFeedback.value = true;
  if (isPKCS10Upload() && pkcs10.value === null) {
    // TODO: Hinweis bei fehlendem Upload hinzufügen
    logger.error("KEIN PKCS10 vorhanden");
    return null;
  }
  if (
    userData.value !== null &&
    (certificateData.value !== null || pkcs10.value !== null) &&
    (!custom.termsAndConditionsRequired || termsAccepted.value) &&
    dataProtectionAgreementAccepted.value &&
    (!publishRequired.value || publish.value)
  ) {
    logger.debug("onSubmit() --> true");
    let pkcs10PEM;
    let privateKey;
    let pinHash = await sha1(userData.value.revokePin);
    if (pkcs10.value !== null) {
      // Bestehender Antrag eingegeben
      pkcs10PEM = pkcs10.value.pem;
      privateKey = ""; // es wird kein privater Schlüssel gesetzt
      certificateData.value.rdns = pkcs10.value.subject;
      certificateData.value.sans = pkcs10.value.altnames;
    } else {
      // Neuer Antrag
      try {
        const builder = new RequestBuilder();
        builder.setSubject(certificateData.value.rdns);
        //builder.setAltnames(certificateData.sans);
        // TODO: Altnames setzen
        builder.setKeytype(certificateData.value.keytype);
        ({ pkcs10PEM, privateKey } = await builder.build());
      } catch (e) {
        logger.error("Fehler beim Erzeugen des PKCS10-Antrags: " + e);
        return null;
      }
      logger.trace("PKCS10-Antrag: " + pkcs10PEM);
    }

    let addName = userData.value.addName;
    let addEmail = userData.value.addEmail;
    let addOrg = userData.value.addOrg;

    if (props.certType === CERT_TYPES.user) {
      // Bei Nutzerzertifikaten nehmen wir addName aus CN
      addName = certificateData.value.rdns.CN.join(", ");
    }

    request.value = new Request({
      ca: custom.ca,
      ra_id: custom.ra_id,
      comment: comment.value,
      date: new Date(),
      addName: addName,
      addEmail: addEmail,
      addOrg: addOrg,
      plainPinHash: pinHash,
      role: role.value,
      publish: publish.value,
      subject: certificateData.value.rdns,
      altnames: certificateData.value.sans,
      serial: "",
      pkcs10PEM: pkcs10PEM,
      plainPrivateKey: privateKey,
      certType: props.certType,
      forgeDependency: false,
      version: 4,
    });

    router.push("2");
  }
}

onMounted(() => {
  if (developerMode) {
    developerModeFunction();
  }

  updatePublish(); // Werte für publish etc. setzen
})
</script>

<style scoped>
#comment_input {
  margin-bottom: 15px;
}
#backbutton {
  margin-top: 10px;
}
</style>
